import Tooltip from "@mui/material/Tooltip";
import { cloneElement, ReactElement, ReactNode } from "react";

interface AppTooltipProps {
    /** The text shown on hover and on keyboard focus. Nothing is shown while it is empty. */
    title: ReactNode;
    placement?: "top" | "bottom" | "left" | "right";
    /**
     * One element that can hold a ref: a button, a span. A disabled button fires no mouse events,
     * so it cannot show a tooltip: pass `disabled` while it is disabled.
     */
    children: ReactElement;
    /**
     * Shows nothing for now, e.g. while its button is disabled or busy. The tooltip stays mounted,
     * so the element is not replaced by a new one (which would drop its focus and busy state).
     */
    disabled?: boolean;
    /** Raises the tooltip above a layer that sits higher than MUI's own (a select's menu portal). */
    zIndex?: number;
}

/**
 * The app's tooltip. Every text shown on hover goes through it, never through the browser's `title`
 * attribute, so all of them look alike and can be restyled in one place.
 */
export function AppTooltip({ title, placement = "top", children, disabled, zIndex }: AppTooltipProps) {
    if (title === null || title === undefined || title === "") return children;
    // `describeChild`: the tooltip describes its element (aria-describedby while open) and never
    // renames it; without it MUI would put the text into aria-label, and "Add" would read "Add
    // location". MUI would also copy the text into the child's `title` while closed, which is the
    // browser's tooltip; the child's own `title: undefined` wins over it.
    return (
        // An empty title keeps MUI from opening the tooltip, and from warning about a disabled button.
        <Tooltip title={disabled ? "" : title} placement={placement} arrow disableInteractive describeChild
            slotProps={zIndex !== undefined ? { popper: { style: { zIndex } } } : undefined}>
            {cloneElement(children, { title: undefined })}
        </Tooltip>
    );
}
